/**
 * Sources Statistics Tab
 * Uses shared CampaignStatsDashboard with sources-specific data.
 * Same layout as Drop, List, and Gateway statistics.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useCallback, useMemo } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';

import type { ListPerformanceTagsResponse } from '@/hooks';
import {
  useAuth,
  useCountry,
  useSourcePerformanceTags,
  useSourcesWithStats,
  useSourcesCrossStats,
} from '@/hooks';
import { SourceListLeafCell } from '@/features/smswiser-campaigns/components/SourcesStatsTab/SourceListLeafCell';

import {
  CampaignStatsDashboard,
  TruncatedSourceCell,
  FILTER_FIELDS_SOURCES,
  GROUP_BY_OPTIONS_SOURCES,
  crossPairForGroupBy,
  buildCrossStatsGroups,
  toRangeISO,
  toGenericStatsRowFromSource,
  useStatsTabState,
  useStatsCompareState,
  useFilteredAndSortedRowsGeneric,
  type GroupByKey,
  type GenericStatsRow,
} from '@/features/smswiser-campaigns/components/SmswiserCampaignStatsTab';

interface SourcesStatsTabProps {
  countryKey: string;
  wiserIds: number[];
  workDate: string;
  /** List perf tags from group-level fetch (Stats section) — shown on Source ▸ Leads list leaves. */
  listPerfTagsQuery: UseQueryResult<ListPerformanceTagsResponse>;
}

export const SourcesStatsTab: FC<SourcesStatsTabProps> = ({
  countryKey,
  wiserIds,
  workDate,
  listPerfTagsQuery,
}) => {
  const { user } = useAuth();
  const country = useCountry(countryKey, workDate);
  // Stable identity — a fresh [] here would bust renderFirstCell's memo.
  const leadsLists = useMemo(
    () => country.data?.leadsLists ?? [],
    [country.data?.leadsLists],
  );
  const state = useStatsTabState(workDate, 'sources');
  const range = useMemo(
    () => toRangeISO(state.fromDate, state.toDate),
    [state.fromDate, state.toDate],
  );

  const perfTagsQuery = useSourcePerformanceTags(
    countryKey,
    wiserIds.length > 0 ? wiserIds : undefined,
    { from: range.from, to: range.to, tz: 'Europe/Paris', currency: state.currency },
  );
  const tagsBySourceId = perfTagsQuery.data?.tagsBySourceId ?? {};
  const tagDetailsBySourceId = perfTagsQuery.data?.tagDetailsBySourceId ?? {};
  const tagColors = perfTagsQuery.data?.tagColors;

  const cross = crossPairForGroupBy(state.groupBy);
  const isCross = cross != null;

  const query = useSourcesWithStats({
    countryKey,
    wiserIds,
    from: range.from,
    to: range.to,
    currency: state.currency,
    tz: 'Europe/Paris',
    enabled: !isCross,
  });

  const crossQuery = useSourcesCrossStats({
    countryKey,
    wiserIds,
    from: range.from,
    to: range.to,
    currency: state.currency,
    tz: 'Europe/Paris',
    cross,
  });

  const activeQuery = isCross ? crossQuery : query;

  const compare = useStatsCompareState(state.fromDate, state.toDate, state.setDateRange);
  const compareRange = useMemo(
    () => toRangeISO(compare.compareFrom, compare.compareTo),
    [compare.compareFrom, compare.compareTo],
  );
  const compareQuery = useSourcesWithStats({
    countryKey,
    wiserIds,
    from: compareRange.from,
    to: compareRange.to,
    currency: state.currency,
    tz: 'Europe/Paris',
    enabled: compare.compareMode && !isCross,
  });
  const compareById = useMemo(() => {
    const map = new Map<string, Record<string, number> | null>();
    (compareQuery.data?.rows ?? []).forEach((r, i) => {
      const g = toGenericStatsRowFromSource(r, i);
      map.set(g.id, g.voluum);
    });
    return map;
  }, [compareQuery.data?.rows]);

  const allSourceRows = query.data?.rows ?? [];
  const singleGenericRows = useMemo(
    () =>
      allSourceRows.map((r, i) => {
        const base = toGenericStatsRowFromSource(r, i);
        const sourceId = r.sourceId ? String(r.sourceId).trim() : '';
        const tags =
          sourceId
            ? (tagsBySourceId[sourceId] ??
              Object.entries(tagsBySourceId).find(
                ([k]) => k.toLowerCase() === sourceId.toLowerCase(),
              )?.[1]) ?? []
            : [];
        return { ...base, performanceTags: tags };
      }),
    [allSourceRows, tagsBySourceId],
  );
  const single = useFilteredAndSortedRowsGeneric(
    singleGenericRows,
    state.filtersState,
    state.sort,
    workDate,
    state.groupBy,
  );

  const crossGroups = useMemo(
    () => buildCrossStatsGroups(crossQuery.data, state.sort),
    [crossQuery.data, state.sort],
  );
  const crossFlatRows = useMemo(
    () => crossGroups.flatMap((g) => g.rows),
    [crossGroups],
  );

  const rows = isCross ? crossFlatRows : single.rows;
  const groups = isCross ? crossGroups : single.groups;
  const totals = activeQuery.data?.totals ?? {};
  const allRowsLength = isCross ? crossFlatRows.length : singleGenericRows.length;

  const availableTags = useMemo(() => {
    const set = new Set<string>();
    for (const name of Object.keys(tagColors ?? {})) if (name.trim()) set.add(name.trim());
    for (const tags of Object.values(tagsBySourceId)) {
      for (const t of tags) if (t && String(t).trim()) set.add(String(t).trim());
    }
    return [...set].sort();
  }, [tagColors, tagsBySourceId]);

  const tagDetailsLookup = useCallback(
    (sourceId: string) => {
      if (!sourceId) return undefined;
      const direct = tagDetailsBySourceId[sourceId];
      if (direct) return direct;
      const entry = Object.entries(tagDetailsBySourceId).find(
        ([k]) => k.toLowerCase() === sourceId.toLowerCase(),
      );
      return entry?.[1];
    },
    [tagDetailsBySourceId],
  );

  const renderFirstCell = useCallback(
    (row: GenericStatsRow) => {
      if (isCross) {
        // Source ▸ Leads list tree: leaf rows are lists — show list-rule tags.
        if (cross === 'source_list') {
          return (
            <SourceListLeafCell
              row={row}
              listTags={listPerfTagsQuery.data}
              leadsLists={leadsLists}
              tagsLoading={listPerfTagsQuery.isLoading}
            />
          );
        }
        return (
          <span className="block truncate text-sm text-foreground">
            {row.label || '—'}
          </span>
        );
      }
      const sourceId = row.id;
      const performanceTags = sourceId
        ? (tagsBySourceId[sourceId] ??
          Object.entries(tagsBySourceId).find(
            ([k]) => k.toLowerCase() === sourceId.toLowerCase(),
          )?.[1])
        : undefined;
      return (
        <TruncatedSourceCell
          sourceId={sourceId}
          label={row.label ?? '—'}
          performanceTags={performanceTags}
          performanceTagDetails={sourceId ? tagDetailsLookup(sourceId) : undefined}
          tagColors={tagColors}
        />
      );
    },
    [
      isCross,
      cross,
      tagsBySourceId,
      tagDetailsLookup,
      tagColors,
      listPerfTagsQuery.data,
      listPerfTagsQuery.isLoading,
      leadsLists,
    ],
  );

  return (
    <CampaignStatsDashboard
      tab="sources"
      title="Sources"
      emptyMessage="No sources found for this range."
      loadingTitle="Loading source performance data"
      loadingDesc="Fetching tracker metrics grouped by source."
      errorPrefix="Failed to load source stats."
      firstColumnLabel="Source"
      firstColumnSortKey="source"
      rowUnitLabel="source"
      filterFields={FILTER_FIELDS_SOURCES}
      groupByOptions={GROUP_BY_OPTIONS_SOURCES}
      rows={rows}
      totals={totals}
      groups={groups}
      totalRowCount={activeQuery.data?.total ?? allRowsLength}
      allRowsLength={allRowsLength}
      currency={activeQuery.data?.currency ?? 'USD'}
      fromDate={state.fromDate}
      toDate={state.toDate}
      onFromChange={state.setFromDate}
      onToChange={state.setToDate}
      onDateRangeChange={state.setDateRange}
      length={state.length}
      onLengthChange={state.setLength}
      filtersState={state.filtersState}
      setFilters={state.setFilters}
      defaultFilter={state.defaultFilter}
      groupBy={state.groupBy}
      setGroupBy={(v) => state.setGroupBy(v as GroupByKey)}
      collapsedGroups={state.collapsedGroups}
      setCollapsedGroups={state.setCollapsedGroups}
      toggleGroup={state.toggleGroup}
      sort={state.sort}
      onSortClick={state.handleSortClick}
      selectedRows={state.selectedRows}
      setSelectedRows={state.setSelectedRows}
      renderFirstCell={renderFirstCell}
      isError={activeQuery.isError ?? false}
      error={activeQuery.error}
      isLoading={activeQuery.isLoading ?? false}
      isFetching={activeQuery.isFetching ?? false}
      dataUpdatedAt={activeQuery.dataUpdatedAt}
      refetch={() => activeQuery.refetch()}
      showLimitSelect
      showGroupBySelect
      tagFilterOptions={availableTags}
      csvExportUserId={user?.id}
      compareCapable={!isCross}
      compareMode={compare.compareMode}
      onToggleCompare={compare.toggleCompareMode}
      compareFromDate={compare.compareFrom}
      compareToDate={compare.compareTo}
      onCompareFromChange={compare.setCompareFrom}
      onCompareToChange={compare.setCompareTo}
      onCompareRangeChange={compare.setCompareRange}
      compareById={compareById}
      compareTotals={compareQuery.data?.totals ?? {}}
      onComparePreset={compare.applyPreset}
      granularity={compare.granularity}
      onGranularityChange={compare.setGranularity}
      countryKey={countryKey}
      wiserIds={wiserIds}
      tagFilterColors={tagColors}
      tagsLoading={perfTagsQuery.isLoading}
    />
  );
};

export default SourcesStatsTab;
